import type { Identifier } from "ra-core";

import type { Company, Contact, ContactNote } from "../types";

/**
 * Search entry point for the bar, and later for an in-app chat assistant.
 * The database RPC `public.global_search` is the one implementation.
 * A1 dossiers/news should add a result type here and a UNION branch in the
 * RPC, not a second search path.
 */
export const GLOBAL_SEARCH_RPC = "global_search";
export const SEARCH_MIN_CHARS = 2;
export const SEARCH_DEBOUNCE_MS = 250;
export const SEARCH_MAX_RESULTS = 20;
export const SEARCH_INPUT_ID = "crm-global-search";

export const SEARCH_TYPES = ["contact", "company", "note"] as const;
export type SearchHitType = (typeof SEARCH_TYPES)[number];

export type GlobalSearchHit = {
  type: SearchHitType;
  id: number;
  title: string;
  subtitle: string | null;
  snippet: string | null;
  rank: number;
  contact_id: number | null;
};

export const isSearchHitType = (value: unknown): value is SearchHitType =>
  typeof value === "string" &&
  (SEARCH_TYPES as readonly string[]).includes(value);

export const normalizeSearchText = (value: string) =>
  value.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase().trim();

export const searchHitPath = (
  hit: Pick<GlobalSearchHit, "type" | "id" | "contact_id">,
) => {
  if (hit.type === "company") return `/companies/${hit.id}/show`;
  if (hit.type === "note" && hit.contact_id != null) {
    return `/contacten/${hit.contact_id}`;
  }
  return `/contacten/${hit.id}`;
};

const asId = (id: Identifier) => {
  const numeric = typeof id === "number" ? id : Number(id);
  return Number.isFinite(numeric) ? numeric : null;
};

const includesQuery = (haystack: string, query: string) =>
  normalizeSearchText(haystack).includes(query);

/**
 * In-memory stand-in for the RPC, used by the demo data provider.
 * ponytail: substring after unaccent. Trigram and full text stay in Postgres.
 */
export const searchRecords = ({
  q,
  contacts,
  companies,
  notes,
  maxResults = SEARCH_MAX_RESULTS,
}: {
  q: string;
  contacts: Contact[];
  companies: Company[];
  notes: ContactNote[];
  maxResults?: number;
}): GlobalSearchHit[] => {
  const nq = normalizeSearchText(q);
  if (nq.length < SEARCH_MIN_CHARS) return [];

  const companyById = new Map(
    companies.flatMap((company) => {
      const id = asId(company.id);
      return id == null ? [] : [[id, company] as const];
    }),
  );
  const contactById = new Map(
    contacts.flatMap((contact) => {
      const id = asId(contact.id);
      return id == null ? [] : [[id, contact] as const];
    }),
  );
  const hits: GlobalSearchHit[] = [];

  for (const contact of contacts) {
    const id = asId(contact.id);
    if (id == null) continue;
    const company =
      contact.company_id == null
        ? undefined
        : companyById.get(Number(contact.company_id));
    const emails = (contact.email_jsonb ?? []).map((item) => item.email);
    const phones = (contact.phone_jsonb ?? []).map((item) => item.number);
    const haystack = [
      contact.first_name,
      contact.last_name,
      company?.name,
      ...emails,
      ...phones,
    ]
      .filter(Boolean)
      .join(" ");
    if (!includesQuery(haystack, nq)) continue;
    hits.push({
      type: "contact",
      id,
      title: [contact.first_name, contact.last_name].filter(Boolean).join(" "),
      subtitle: company?.name ?? emails[0] ?? null,
      snippet: phones[0] ?? null,
      rank: normalizeSearchText(haystack).startsWith(nq) ? 2 : 1,
      contact_id: id,
    });
  }

  for (const company of companies) {
    const id = asId(company.id);
    if (id == null) continue;
    const haystack = [company.name, company.city, company.website]
      .filter(Boolean)
      .join(" ");
    if (!includesQuery(haystack, nq)) continue;
    hits.push({
      type: "company",
      id,
      title: company.name,
      subtitle:
        [company.city, company.website].filter(Boolean).join(" · ") || null,
      snippet: company.description ? company.description.slice(0, 160) : null,
      rank: 1,
      contact_id: null,
    });
  }

  for (const note of notes) {
    const id = asId(note.id);
    const contactId = asId(note.contact_id);
    if (id == null || !note.text || !includesQuery(note.text, nq)) continue;
    const contact = contactId == null ? undefined : contactById.get(contactId);
    hits.push({
      type: "note",
      id,
      title: note.text.slice(0, 80),
      subtitle: contact
        ? [contact.first_name, contact.last_name].filter(Boolean).join(" ")
        : null,
      snippet: note.text.slice(0, 160),
      rank: 1,
      contact_id: contactId,
    });
  }

  return hits
    .sort((a, b) => b.rank - a.rank || a.type.localeCompare(b.type))
    .slice(0, maxResults);
};
